import type { Event, Exception } from '@sentry/core';
import { documentPathnames, documentUrlOf, resolveFrameUrl } from './frameUrl.js';

/** Where the throwing code came from. `mixed`: our bundle and foreign code share the stack, so it is still ours. */
export type ErrorSource = 'app' | 'vendor' | 'injected' | 'extension' | 'mixed' | 'unknown';

export interface ErrorOrigin {
  source: ErrorSource;
  /** Host of the innermost foreign frame, absent when that frame is inline or there is none. */
  vendor?: string;
}

/** Must match `build.assetsDir` in `apps/hub/vite.config.ts`. */
const ASSET_PATH = '/_assets/';

const SDK_CHUNK = `${ASSET_PATH}vendor-sentry-`;

/** A scheme or an absolute path. Sentry's sentinels (`native`, `?`) would resolve against the document and read as ours. */
const ATTRIBUTABLE_FILENAME = /^([a-z][a-z0-9+.-]*:|\/)/i;

/** Not "everything non-web": `blob:` and `data:` carry no host and are as likely ours (a worker off `createObjectURL`). */
const OUT_OF_PAGE_PROTOCOL = /-extension:$|^webkit-masked-url:$/;

type FrameSource = Exclude<ErrorSource, 'mixed' | 'unknown'>;

interface FrameOrigin {
  source: FrameSource;
  vendor?: string;
}

function classifyFrame(filename: string, documentPage: URL | undefined, documentPaths: Set<string>): FrameOrigin | undefined {
  if (!ATTRIBUTABLE_FILENAME.test(filename)) {
    return undefined;
  }

  const url = resolveFrameUrl(filename, documentPage);
  if (!url) {
    return undefined;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return OUT_OF_PAGE_PROTOCOL.test(url.protocol) ? { source: 'extension', vendor: url.host || url.protocol.replace(':', '') } : undefined;
  }
  // Origin has to match too, otherwise a vendor serving from its own `/_assets/` would read as ours.
  if (documentPage?.origin === url.origin) {
    if (documentPaths.has(url.pathname)) {
      return { source: 'injected' };
    }
    if (url.pathname.startsWith(SDK_CHUNK)) {
      return undefined;
    }
    if (url.pathname.startsWith(ASSET_PATH)) {
      return { source: 'app' };
    }
  }
  return { source: 'vendor', vendor: url.host };
}

export function classifyErrorOrigin(event: Event): ErrorOrigin {
  // No exception at all: a captureMessage/trackEvent call, and only our own code makes those.
  if (!event.exception) {
    return { source: 'app' };
  }

  const documentUrl = documentUrlOf(event);
  const documentPage = documentUrl ? resolveFrameUrl(documentUrl) : undefined;
  const documentPaths = documentPathnames(documentPage);
  // A stack-overflow stack repeats one filename dozens of times.
  const seen = new Map<string, FrameOrigin | undefined>();

  let sawApp = false;
  let foreign: FrameOrigin | undefined;

  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      const filename = frame.filename ?? frame.abs_path;
      if (!filename) {
        continue;
      }
      if (!seen.has(filename)) {
        seen.set(filename, classifyFrame(filename, documentPage, documentPaths));
      }
      const origin = seen.get(filename);
      if (!origin) {
        continue;
      }
      if (origin.source === 'app') {
        sawApp = true;
        continue;
      }
      // Sentry orders frames oldest first: the last foreign frame is the one that threw.
      foreign = origin;
    }
  }

  if (!foreign) {
    return sawApp ? { source: 'app' } : { source: 'unknown' };
  }
  return { source: sawApp ? 'mixed' : foreign.source, vendor: foreign.vendor };
}

/** Sentry orders exception values outermost first: the last one is what actually threw. */
function lastException(event: Event): Exception | undefined {
  const values = event.exception?.values;
  return values?.[values.length - 1];
}

/** Payment failures cost money: a critical vendor keeps its own issues and is never dropped. */
function isExemptVendor(origin: ErrorOrigin, criticalVendors: readonly string[]): boolean {
  return Boolean(origin.vendor && criticalVendors.includes(origin.vendor));
}

/** Browser wording for a fetch that never reached the server. Substring: browsers append detail. */
const NETWORK_FAILURE = /Failed to fetch|Network request failed|Load failed/;

/** True for a transport failure in foreign code, which arrives with no stack to judge it by. */
export function isForeignNetworkNoise(event: Event, origin: ErrorOrigin, criticalVendors: readonly string[] = []): boolean {
  if (origin.source === 'app' || origin.source === 'mixed' || isExemptVendor(origin, criticalVendors)) {
    return false;
  }
  return NETWORK_FAILURE.test(lastException(event)?.value ?? '');
}

/** Collapses foreign errors to one issue per vendor and type. Anything with a frame of ours keeps default grouping. */
export function foreignErrorFingerprint(event: Event, origin: ErrorOrigin, criticalVendors: readonly string[] = []): string[] | undefined {
  if (origin.source === 'app' || origin.source === 'mixed' || origin.source === 'unknown' || isExemptVendor(origin, criticalVendors)) {
    return undefined;
  }
  return ['third-party', origin.source, origin.vendor ?? 'inline', lastException(event)?.type ?? 'Error'];
}
